import { useState, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { invalidateIndicatorCaches } from "@/lib/cache-invalidation";
import type { DocumentPage, PageIndicator, DocumentUser } from "@shared/schema";

interface UseCommentIndicatorMutationsProps {
  currentPageData: DocumentPage | undefined;
  currentUser: { id: string; name: string; email: string } | null;
  document: { defaultIndicatorColor: string };
  pageIndicators: PageIndicator[];
  activeIndicator: PageIndicator | null;
  pendingIndicator: {
    x: number;
    y: number;
    canvasX: number;
    canvasY: number;
    number: number;
  } | null;
  setActiveIndicator: (indicator: PageIndicator | null) => void;
  setPendingIndicator: (indicator: any) => void;
  setNewComment: (comment: string) => void;
  deleteIndicatorMutation: any;
  documentId: string;
  versionNumber: number;
  versionId?: string;
  // Indicator-specific drawing box state
  indicatorDrawingBoxes: Map<number, { startX: number; startY: number; endX: number; endY: number }>;
  setIndicatorDrawingBoxes: (updater: (prev: Map<number, { startX: number; startY: number; endX: number; endY: number }>) => Map<number, { startX: number; startY: number; endX: number; endY: number }>) => void;
}

export function useCommentIndicatorMutations({
  currentPageData,
  currentUser,
  document,
  pageIndicators,
  activeIndicator,
  pendingIndicator,
  setActiveIndicator,
  setPendingIndicator,
  setNewComment,
  deleteIndicatorMutation,
  documentId,
  versionNumber,
  versionId,
  indicatorDrawingBoxes,
  setIndicatorDrawingBoxes,
}: UseCommentIndicatorMutationsProps) {

  const addCommentMutation = useMutation({
    mutationFn: async (variables: { content: string; drawingBox?: { startX: number; startY: number; endX: number; endY: number } | null; mentionedUserIds?: string[] }) => {
      const { content, drawingBox, mentionedUserIds } = variables;
      
      // Read from indicator-specific state if pendingIndicator exists and no drawingBox provided
      let finalDrawingBox = drawingBox;
      if (pendingIndicator && !finalDrawingBox && indicatorDrawingBoxes.has(pendingIndicator.number)) {
        finalDrawingBox = indicatorDrawingBoxes.get(pendingIndicator.number);
        console.log(`📦 Using drawing box for indicator #${pendingIndicator.number}:`, finalDrawingBox);
      }
      
      // IRONCLAD VALIDATION: Create indicator AND comment together
      if (pendingIndicator) {
        // First create the indicator with optional drawing box
        const indicatorPayload = {
          x: pendingIndicator.x,
          y: pendingIndicator.y,
          number: pendingIndicator.number,
          color: document.defaultIndicatorColor,
          ...(finalDrawingBox && { drawingBox: finalDrawingBox })
        };
        
        const indicatorResponse = await fetch(`/api/pages/${currentPageData?.id}/indicators`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(indicatorPayload),
        });
        if (!indicatorResponse.ok) throw new Error("Failed to create indicator");
        const newIndicator = await indicatorResponse.json();
        
        // Then immediately add the comment
        const commentResponse = await fetch(`/api/indicators/${newIndicator.id}/comments`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ 
            content, 
            userId: currentUser?.id,
            mentionedUserIds: mentionedUserIds ?? [],
          }),
        });
        if (!commentResponse.ok) {
          // If comment creation fails, delete the indicator
          await fetch(`/api/pages/${currentPageData?.id}/indicators/${newIndicator.id}`, {
            method: "DELETE",
          });
          throw new Error("Failed to add comment");
        }
        
        return { indicator: newIndicator, comment: await commentResponse.json() };
      } else {
        // Existing indicator - just add comment
        const response = await fetch(`/api/indicators/${activeIndicator?.id}/comments`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ 
            content, 
            userId: currentUser?.id,
            mentionedUserIds: mentionedUserIds ?? [],
          }),
        });
        if (!response.ok) throw new Error("Failed to add comment");
        return { comment: await response.json() };
      }
    },
    onMutate: async (variables: { content: string; drawingBox?: any }) => {
      // Clear comment input immediately
      setNewComment("");
      
      // Determine target indicator ID and query key
      const targetIndicatorId = activeIndicator?.id;
      const commentsQueryKey = ["/api/indicators", targetIndicatorId, "comments"];
      const threadQueryKey = ["/api/pages", currentPageData?.id, "indicators-with-comments"];
      const indicatorsQueryKey = ["/api/pages", currentPageData?.id, "indicators"];
      
      // Cancel outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: commentsQueryKey });
      await queryClient.cancelQueries({ queryKey: threadQueryKey });
      await queryClient.cancelQueries({ queryKey: indicatorsQueryKey });
      
      // Snapshot previous values for rollback
      const previousComments = queryClient.getQueryData(commentsQueryKey);
      const previousThread = queryClient.getQueryData(threadQueryKey);
      const previousIndicators = queryClient.getQueryData(indicatorsQueryKey);
      
      // Optimistic update for BOTH existing and pending indicators
      if (currentUser && currentPageData?.id) {
        const timestamp = Date.now();
        
        if (pendingIndicator) {
          // For pending indicators: create temporary indicator with comment
          const tempIndicatorId = `temp-indicator-${timestamp}`;
          const optimisticComment = {
            id: `temp-comment-${timestamp}`,
            indicatorId: tempIndicatorId,
            userId: currentUser.id,
            content: variables.content,
            createdAt: new Date(),
            user: currentUser,
            attachments: [],
          };
          
          const tempIndicator = {
            id: tempIndicatorId,
            pageId: currentPageData.id,
            number: pendingIndicator.number,
            x: pendingIndicator.x,
            y: pendingIndicator.y,
            color: document.defaultIndicatorColor,
            status: 'open' as const,
            drawingBox: indicatorDrawingBoxes.get(pendingIndicator.number) || null,
            comments: [optimisticComment],
          };
          
          // Add temporary indicator to thread query (with comments)
          queryClient.setQueryData(threadQueryKey, (old: any) => [...(old || []), tempIndicator]);
          
          // CRITICAL: Also add to indicators query (for drawing overlay to render boxes immediately)
          const tempIndicatorForOverlay = {
            id: tempIndicatorId,
            pageId: currentPageData.id,
            number: pendingIndicator.number,
            x: pendingIndicator.x,
            y: pendingIndicator.y,
            color: document.defaultIndicatorColor,
            status: 'open' as const,
            drawingBox: indicatorDrawingBoxes.get(pendingIndicator.number) || null,
          };
          queryClient.setQueryData(indicatorsQueryKey, (old: any) => [...(old || []), tempIndicatorForOverlay]);
        } else if (targetIndicatorId) {
          // For existing indicators: add comment to existing indicator
          const optimisticComment = {
            id: `temp-comment-${timestamp}`,
            indicatorId: targetIndicatorId,
            userId: currentUser.id,
            content: variables.content,
            createdAt: new Date(),
            user: currentUser,
            attachments: [],
          };
          
          // Update the simple comments query
          queryClient.setQueryData(commentsQueryKey, (old: any) => [...(old || []), optimisticComment]);
          
          // Update the thread query (indicators-with-comments)
          queryClient.setQueryData(threadQueryKey, (old: any) => {
            if (!old) return old;
            return old.map((indicator: any) => 
              indicator.id === targetIndicatorId
                ? { ...indicator, comments: [...(indicator.comments || []), optimisticComment] }
                : indicator
            );
          });
        }
      }
      
      // Store queryKeys and data in context for reliable rollback
      return { 
        previousComment: variables.content, 
        previousComments,
        previousThread,
        previousIndicators,
        commentsQueryKey,
        threadQueryKey,
        indicatorsQueryKey,
        targetIndicatorId
      };
    },
    onSuccess: (data, variables, context) => {
      // FIXED: Save drawing box to localStorage on successful comment submission (proper persistence)
      if (pendingIndicator) {
        const drawingBox = indicatorDrawingBoxes.get(pendingIndicator.number);
        
        if (drawingBox && currentPageData?.id) {
          // FIXED: Only add this specific indicator's box to localStorage (don't persist other temporary boxes)
          const storageKey = `indicatorDrawingBoxes_${currentPageData.id}`;
          const savedBoxes = JSON.parse(localStorage.getItem(storageKey) || '{}');
          savedBoxes[pendingIndicator.number] = drawingBox;
          localStorage.setItem(storageKey, JSON.stringify(savedBoxes));
          console.log(`✅ Persisted drawing box for indicator #${pendingIndicator.number} to localStorage after successful comment submission:`, drawingBox);
        }
        
        // Clear from React state since it's now persisted
        setIndicatorDrawingBoxes(prev => {
          const newMap = new Map(prev);
          newMap.delete(pendingIndicator.number);
          return newMap;
        });
      }
      
      if (pendingIndicator && data.indicator) {
        // Switch from pending to real indicator
        setActiveIndicator(data.indicator);
        setPendingIndicator(null);
        if (currentPageData) {
          invalidateIndicatorCaches(currentPageData.id, documentId, versionNumber, versionId);
        }
      }
      
      // Invalidate the correct query using the indicator ID from the response or context
      const responseIndicatorId = data.comment?.indicatorId || data.indicator?.id;
      const targetIndicatorId = responseIndicatorId || context?.targetIndicatorId;
      if (targetIndicatorId) {
        queryClient.invalidateQueries({ queryKey: ["/api/indicators", targetIndicatorId, "comments"] });
      }
      
      // Refetch PageCommentsSection to show new comment instantly
      if (currentPageData) {
        queryClient.invalidateQueries({ queryKey: ["/api/pages", currentPageData.id, "indicators-with-comments"] });
      }
    },
    onError: (error, variables, context) => {
      // Restore comment input if submission failed
      if (context?.previousComment) {
        setNewComment(context.previousComment);
      }
      
      // Rollback all optimistic updates using queryKeys from context
      if (context?.previousComments && context?.commentsQueryKey) {
        queryClient.setQueryData(context.commentsQueryKey, context.previousComments);
      }
      if (context?.previousThread && context?.threadQueryKey) {
        queryClient.setQueryData(context.threadQueryKey, context.previousThread);
      }
      if (context?.previousIndicators && context?.indicatorsQueryKey) {
        queryClient.setQueryData(context.indicatorsQueryKey, context.previousIndicators);
      }
      
      // Clean up temporary drawing box on failure
      if (pendingIndicator) {
        setIndicatorDrawingBoxes(prev => {
          const newMap = new Map(prev);
          newMap.delete(pendingIndicator.number);
          return newMap;
        });
        console.log(`🗑️ Cleaned up temporary drawing box for failed indicator #${pendingIndicator.number}`);
      }
    }
  });

  // Handle low rating indicator creation
  const handleLowRatingIndicator = useCallback((rating: number, position: { x: number; y: number }) => {
    if (!currentPageData) return;
    if (!position || typeof position.x !== 'number' || typeof position.y !== 'number') return;
    
    // Calculate next indicator number safely
    const existingNumbers = (pageIndicators || [])
      .map(i => i.number)
      .filter(num => typeof num === 'number' && !isNaN(num));
    const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
    
    // Create pending indicator at calculated position
    setPendingIndicator({
      x: position.x,
      y: position.y,
      canvasX: position.x, // For now, use image coords as canvas coords (will be transformed during render)
      canvasY: position.y,
      number: nextNumber
    });
    
    // Pre-populate comment text
    const ratingTexts = {
      1: "I gave this a 1-star because...",
      2: "I gave this a 2-star because...", 
      3: "I gave this a 3-star because..."
    };
    setNewComment(ratingTexts[rating as keyof typeof ratingTexts] || "");
    
    // Auto-focus will happen via existing useEffect
  }, [currentPageData, pageIndicators, setPendingIndicator, setNewComment]);

  const deleteCommentMutation = useMutation({
    mutationFn: async (commentId: string) => {
      const response = await fetch(`/api/comments/${commentId}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Failed to delete comment");
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["/api/indicators", activeIndicator?.id, "comments"] });
      
      // Check if this was the last comment - if so, delete the indicator
      const commentsResponse = await fetch(`/api/indicators/${activeIndicator?.id}/comments`);
      const remainingComments = await commentsResponse.json();
      
      if (!remainingComments || remainingComments.length === 0) {
        // OPTIMISTIC UPDATE: Immediately remove indicator from UI before server deletion
        if (activeIndicator && currentPageData?.id) {
          // Clear UI state immediately
          setActiveIndicator(null);
          
          // Optimistically remove indicator from cache for immediate visual removal
          queryClient.setQueryData<PageIndicator[]>(
            ["/api/pages", currentPageData.id, "indicators"],
            (indicators) => indicators?.filter(i => i.id !== activeIndicator.id) || []
          );
          
          // Also remove from indicators-with-comments to prevent stale refetch race
          queryClient.setQueryData(
            ["/api/pages", currentPageData.id, "indicators-with-comments"],
            (old: any) => old?.filter((i: any) => i.id !== activeIndicator.id) || []
          );
          
          // Clean up from React state
          setIndicatorDrawingBoxes(prev => {
            const newMap = new Map(prev);
            newMap.delete(activeIndicator.number);
            return newMap;
          });
          
          // FIXED: Clean up from localStorage using read-modify-write pattern to prevent data loss
          const storageKey = `indicatorDrawingBoxes_${currentPageData.id}`;
          const savedBoxes = JSON.parse(localStorage.getItem(storageKey) || '{}');
          delete savedBoxes[activeIndicator.number];
          localStorage.setItem(storageKey, JSON.stringify(savedBoxes));
          
          console.log(`🚀 Optimistically removed indicator #${activeIndicator.number} from UI during comment deletion`);
        }
        
        // No comments left = delete the indicator (server-side)
        deleteIndicatorMutation.mutate(activeIndicator!.id);
      }
    }
  });

  return {
    addCommentMutation,
    handleLowRatingIndicator,
    deleteCommentMutation: deleteCommentMutation,
  };
}